import React, { useState, useContext } from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../../../../shadcn/components/ui/Dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../../shadcn/components/ui/Table';
import { toast } from 'sonner';
import { Button } from '../../../../shadcn/components/ui/Button';
import { Input } from '../../../../shadcn/components/ui/Input';
import { Checkbox } from '../../../../shadcn/components/ui/Checkbox';
import { Label } from '../../../../shadcn/components/ui/Label';
import { Card, CardContent, CardHeader, CardTitle } from '../../../../shadcn/components/ui/Card';
import { Separator } from '../../../../shadcn/components/ui/Separator';
import { warehouseApi as api } from '../../../../api/warehouse';

const ChildLocationsModal = ({ parentLocation, isOpen, onClose, onChildLocationUpdated }) => {
    const childLocations = parentLocation?.childLocations || [];
    const [lineNumber, setLineNumber] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [updatingLocation, setUpdatingLocation] = useState(false);

    if (!parentLocation) return null;

    const handleActiveToggle = async (childLocation, newActiveStatus) => {
        if (updatingLocation) return;

        setUpdatingLocation(true);

        try {
            const response = await api.updateLocations({
                ids: [childLocation.id],
                active: newActiveStatus,
            });

            if (response.status === false) {
                toast.error(`Error updating child location: ${response.msg}`);
                return;
            }

            toast.success('Child location updated successfully!');

            // Notify parent component
            if (onChildLocationUpdated) {
                onChildLocationUpdated();
            }
        } catch (error) {
            toast.error('Error updating child location');
        } finally {
            setUpdatingLocation(false);
        }
    };

    const handleDownloadLabel = async (childLocationId) => {
        try {
            await api.bulkDownloadLocationLabels([childLocationId]);
        } catch (error) {
            toast.error('Error downloading label');
        }
    };

    const handleAddChildLocation = async (e) => {
        e.preventDefault();

        if (!lineNumber.trim()) {
            toast.error('Line number is required');
            return;
        }

        setIsSubmitting(true);

        try {
            const response = await api.createReplenishmentChildLocation({
                parentLocationId: parentLocation.id,
                lineNumber,
            });

            if (response.status === false) {
                toast.error(`Error creating child location: ${response.msg}`);
                return;
            }

            setLineNumber('');

            toast.success('Child location created successfully!');

            // Notify parent component
            if (onChildLocationUpdated) {
                onChildLocationUpdated();
            }
        } catch (error) {
            toast.error('Error creating child location');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent
                className="max-w-5xl max-h-[80vh] overflow-y-auto"
                style={{ width: '50vw', maxWidth: '1200px' }}
            >
                <DialogHeader className="border-b border-gray-200 pb-4 mb-6">
                    <DialogTitle className="text-2xl font-bold text-gray-800 flex items-center gap-3">
                        Replenishment Child Locations for {parentLocation.name}
                    </DialogTitle>
                </DialogHeader>

                <div className="space-y-6">
                    <Card className="border-blue-200 pb-2 pt-0">
                        <CardHeader className="bg-blue-100 rounded-t-xl pt-3">
                            <CardTitle className="text-lg text-blue-900 flex items-center gap-2">
                                <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                                Existing Child Locations ({childLocations.length})
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="bg-white">
                            {childLocations.length === 0 ? (
                                <div className="text-center py-12">
                                    <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                                        <span className="text-2xl text-gray-400">📍</span>
                                    </div>
                                    <p className="text-gray-500 text-lg">No child locations found</p>
                                    <p className="text-gray-400 text-sm mt-1">
                                        Add a new child location below to get started
                                    </p>
                                </div>
                            ) : (
                                <Table>
                                    <TableHeader>
                                        <TableRow className="border-blue-200">
                                            <TableHead className="text-blue-900 font-semibold">Name</TableHead>
                                            <TableHead className="text-blue-900 font-semibold">Active</TableHead>
                                            <TableHead className="text-blue-900 font-semibold">Label</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {childLocations.map((childLocation, index) => (
                                            <TableRow key={childLocation.id} className={`hover:bg-blue-50`}>
                                                <TableCell className="font-medium text-gray-800">
                                                    {childLocation.name}
                                                </TableCell>
                                                <TableCell>
                                                    <div className="flex items-center space-x-2">
                                                        <Checkbox
                                                            id={`active-${childLocation.id}`}
                                                            checked={childLocation.active}
                                                            disabled={updatingLocation}
                                                            onCheckedChange={(checked) =>
                                                                handleActiveToggle(childLocation, checked)
                                                            }
                                                            className="data-[state=checked]:bg-green-500 data-[state=checked]:border-green-500"
                                                        />
                                                        <Label
                                                            htmlFor={`active-${childLocation.id}`}
                                                            className={`text-sm font-medium ${
                                                                childLocation.active
                                                                    ? 'text-green-700'
                                                                    : 'text-gray-500'
                                                            }`}
                                                        >
                                                            {childLocation.active ? 'Active' : 'Inactive'}
                                                        </Label>
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        className="border-blue-300 text-blue-700 hover:bg-blue-100 hover:border-blue-400"
                                                        onClick={() => handleDownloadLabel(childLocation.id)}
                                                    >
                                                        Download Label
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                        </CardContent>
                    </Card>

                    <Separator className="bg-gradient-to-r from-transparent via-gray-300 to-transparent" />

                    {/* Add New Child Location Form */}
                    <Card className="gap-2 border-green-200 pb-2 pt-0">
                        <CardHeader className="bg-green-100 rounded-t-xl pt-3">
                            <CardTitle className="text-lg text-green-900 flex items-center gap-2">
                                <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                                Add New Child Location
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="bg-white">
                            <form onSubmit={handleAddChildLocation} className="space-y-4">
                                <div className="space-y-2">
                                    <Label htmlFor="childLocationName" className="pb-4 text-green-800 font-medium">
                                        Child Location Line Number
                                    </Label>
                                    <Input
                                        id="lineNumber"
                                        type="text"
                                        placeholder="Enter child location line number..."
                                        value={lineNumber}
                                        onChange={(e) => setLineNumber(e.target.value)}
                                        disabled={isSubmitting}
                                        className="border-green-300 focus:border-green-500 focus:ring-green-500/20"
                                    />
                                </div>

                                <Button
                                    type="submit"
                                    disabled={isSubmitting || !lineNumber.trim()}
                                    className="w-full bg-green-600 hover:bg-green-700 text-white font-medium"
                                >
                                    {isSubmitting ? (
                                        <span className="flex items-center gap-2">
                                            <span className="animate-spin">⏳</span>
                                            Creating...
                                        </span>
                                    ) : (
                                        <span className="flex items-center gap-2">➕ Create Child Location</span>
                                    )}
                                </Button>
                            </form>
                        </CardContent>
                    </Card>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default ChildLocationsModal;
